import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { fetchAllPolicies } from '../api/policies';
import { DEFAULT_POLICY_FILTER, type Policy, type PolicyFilter } from '../types';
import {
  countPoliciesByLevel,
  filterPolicies,
  sortPolicies,
} from '../utils/policyHelpers';

export interface UsePoliciesResult {
  policies: Policy[];
  allPolicies: Policy[];
  loading: boolean;
  error: string | null;
  filter: PolicyFilter;
  setFilter: Dispatch<SetStateAction<PolicyFilter>>;
  statistics: {
    total: number;
    national: number;
    provincial: number;
    city: number;
    local: number;
  };
}

export function usePolicies(): UsePoliciesResult {
  const [allPolicies, setAllPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<PolicyFilter>(DEFAULT_POLICY_FILTER);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await fetchAllPolicies();
      setAllPolicies(items);
    } catch (e) {
      const message =
        e instanceof Error ? e.message : '政策数据加载失败，请稍后重试';
      setError(message);
      setAllPolicies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const policies = useMemo(() => {
    const filtered = filterPolicies(allPolicies, filter);
    return sortPolicies(filtered, filter.sortBy, filter.keyword);
  }, [allPolicies, filter]);

  const statistics = useMemo(() => countPoliciesByLevel(policies), [policies]);

  return {
    policies,
    allPolicies,
    loading,
    error,
    filter,
    setFilter,
    statistics,
  };
}
